import {createPortal} from 'react-dom';
import {Fragment, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {m, AnimatePresence} from 'framer-motion';
import {useSettings} from '@common/core/settings/use-settings';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {useSocialLogin} from '@common/auth/requests/use-social-login';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {useAuth} from '@common/auth/use-auth';
import {GoogleIcon} from '@common/icons/social/google';
import {FacebookIcon} from '@common/icons/social/facebook';
import {TwitterIcon} from '@common/icons/social/twitter';
import {EnvatoIcon} from '@common/icons/social/envato';
import {rootEl} from '@common/core/root-el';
import type {AuthModalMode} from './auth-modal-context';
import './auth-modal.css';

interface Props {
  mode: AuthModalMode;
  subtitle?: string;
  savedPrompt?: string;
  redirectTo?: string;
  onClose: () => void;
}

export function AuthModal({mode, subtitle, savedPrompt, redirectTo, onClose}: Props) {
  const {branding, social, registration} = useSettings();
  const isDarkMode = useIsDarkMode();
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();
  const {loginWithSocial} = useSocialLogin();
  const [showAll, setShowAll] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  const isRegister = mode === 'register';
  const heading = isRegister
    ? `Create a ${branding.site_name} account`
    : `Log in to ${branding.site_name}`;

  const destination = redirectTo || getRedirectUri();

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleSocial = async (service: Parameters<typeof loginWithSocial>[0]) => {
    const result = await loginWithSocial(service);
    if (result?.status === 'SUCCESS' || result?.status === 'ALREADY_LOGGED_IN') {
      onClose();
      navigate(destination, {replace: true});
    }
  };

  const handleEmail = () => {
    onClose();
    navigate(isRegister ? '/register' : '/login');
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === backdropRef.current) onClose();
  };

  const allSocialsDisabled =
    !social?.google?.enable &&
    !social?.facebook?.enable &&
    !social?.twitter?.enable &&
    !social?.envato?.enable;

  // Build ordered list of social providers
  type Provider = {
    key: Parameters<typeof loginWithSocial>[0];
    label: string;
    icon: JSX.Element;
  };
  const providers: Provider[] = [
    social?.google?.enable && {
      key: 'google',
      label: 'Continue with Google',
      icon: <GoogleIcon viewBox="0 0 48 48" />,
    },
    social?.facebook?.enable && {
      key: 'facebook',
      label: 'Continue with Facebook',
      icon: <FacebookIcon className="text-facebook" />,
    },
    social?.twitter?.enable && {
      key: 'twitter',
      label: 'Continue with X (Twitter)',
      icon: <TwitterIcon className="text-twitter" />,
    },
    social?.envato?.enable && {
      key: 'envato',
      label: 'Continue with Envato',
      icon: <EnvatoIcon viewBox="0 0 50 50" className="text-envato" />,
    },
  ].filter(Boolean) as Provider[];

  // First 2 visible by default, rest toggled
  const visibleProviders = showAll ? providers : providers.slice(0, 2);
  const hasMore = providers.length > 2;

  const toggleLabel = showAll ? 'View less options' : 'View more options';

  const logo = isDarkMode ? branding.logo_light : branding.logo_dark;

  return createPortal(
    <div
      className="am-backdrop"
      ref={backdropRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={heading}
    >
      <m.div
        className="am-card"
        initial={{opacity: 0, scale: 0.96, y: 6}}
        animate={{opacity: 1, scale: 1, y: 0}}
        exit={{opacity: 0, scale: 0.96, y: 6}}
        transition={{duration: 0.22, ease: [0.16, 1, 0.3, 1]}}
      >
        {/* Close */}
        <button className="am-close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Saved prompt banner */}
        {(subtitle || savedPrompt) && (
          <m.div
            className="am-prompt-banner"
            initial={{opacity: 0, y: -6}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.05, duration: 0.3, ease: [0.16, 1, 0.3, 1]}}
          >
            <span className="am-prompt-banner__icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </span>
            <div className="am-prompt-banner__body">
              {subtitle && <p className="am-prompt-banner__title">{subtitle}</p>}
              {savedPrompt && (
                <p className="am-prompt-banner__prompt">&ldquo;{savedPrompt}&rdquo;</p>
              )}
            </div>
          </m.div>
        )}

        {/* Logo */}
        {logo && (
          <img src={logo} alt={branding.site_name} className="am-logo" />
        )}

        {/* Heading */}
        <h2 className="am-heading">{heading}</h2>

        {/* Social + Email buttons */}
        <div className="am-buttons">
          {visibleProviders.map(p => (
            <button
              key={p.key}
              className="am-btn"
              onClick={() => handleSocial(p.key)}
              type="button"
            >
              <span className="am-btn__icon">{p.icon}</span>
              {p.label}
            </button>
          ))}

          {/* Continue with Email always shown */}
          <button className="am-btn" type="button" onClick={handleEmail}>
            <span className="am-btn__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M2 8l10 7 10-7" />
              </svg>
            </span>
            Continue with Email
          </button>
        </div>

        {/* Toggle more/less */}
        {!allSocialsDisabled && hasMore && (
          <button
            className="am-toggle"
            type="button"
            onClick={() => setShowAll(v => !v)}
          >
            {toggleLabel}
          </button>
        )}

        {/* Divider */}
        <hr className="am-divider" />

        {/* Switch mode message */}
        <p className="am-message">
          {isRegister ? (
            <>
              Already have an account?{' '}
              <Link to="/login" onClick={onClose}>Log in</Link>
            </>
          ) : (
            <>
              New to {branding.site_name}?{' '}
              {!registration.disable && (
                <Link to="/register" onClick={onClose}>Create account</Link>
              )}
            </>
          )}
        </p>

        {/* Legal */}
        <p className="am-legal">
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>{' '}
          apply.
        </p>
      </m.div>
    </div>,
    rootEl,
  );
}
